import { request } from '@umijs/max';

const QUANT_API_URL = process.env.QUANT_API_URL || '';

// ============ Deposit types ============

export interface DepositOrder {
  _id: string;
  orderId: string;
  userId: string;
  walletAddress: string;
  amount: string;
  amountFormatted: string;
  chainId: number;
  txHash?: string;
  blockNumber?: number;
  confirmations?: number;
  status: 'pending' | 'confirming' | 'confirmed' | 'expired' | 'failed';
  createdAt: string;
  expiresAt: string;
  confirmedAt?: string;
  manualConfirm?: boolean;
  confirmedBy?: string;
  confirmRemark?: string;
}

export interface DepositListData {
  orders: DepositOrder[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminDepositQuery {
  page?: number;
  pageSize?: number;
  walletAddress?: string;
  status?: string;
}

// Get admin deposits list
export async function getAdminDeposits(
  params?: AdminDepositQuery,
): Promise<{ success: boolean; message: string; data?: DepositListData }> {
  return request(`${QUANT_API_URL}/api/admin/deposits`, {
    method: 'GET',
    params,
    skipErrorHandler: true,
  });
}

// Admin confirm a deposit order manually
export async function adminConfirmDeposit(
  orderId: string,
  remark?: string,
): Promise<{ success: boolean; message: string; data?: DepositOrder }> {
  return request(`${QUANT_API_URL}/api/admin/deposits/${orderId}/confirm`, {
    method: 'POST',
    data: { remark: remark || '' },
    skipErrorHandler: true,
  });
}

// Export deposits as CSV
export async function exportDeposits(params?: { walletAddress?: string; status?: string }) {
  return request(`${QUANT_API_URL}/api/admin/deposits/export`, {
    method: 'GET',
    params,
    responseType: 'blob',
    skipErrorHandler: true,
  });
}

// ============ Custody types ============

export interface CustodyProduct {
  id: string;
  nameZh: string;
  nameEn: string;
  descriptionZh: string;
  descriptionEn: string;
  dailyRate: number;
  duration: number;
  minAmount: number;
  maxAmount: number;
  status: 'active' | 'inactive';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustodyOrder {
  id: string;
  userId: string;
  walletAddress?: string;
  productId: string;
  productNameZh: string;
  productNameEn: string;
  amount: number;
  dailyRate: number;
  dailyYield: number;
  duration: number;
  status: 'active' | 'completed' | 'cancelled';
  claimedCount: number;
  totalClaimed: number;
  lastClaimedAt?: string;
  principalWithdrawn: boolean;
  startAt: string;
  expireAt: string;
  completedAt?: string;
  cancelledAt?: string;
  cancelRemark?: string;
  cancelledBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustodyProductListData {
  products: CustodyProduct[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CustodyOrderListData {
  orders: CustodyOrder[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CustodyStats {
  totalProducts: number;
  activeProducts: number;
  totalOrders: number;
  activeOrders: number;
  totalInvested: number;
  totalYieldPaid: number;
}

export interface CreateProductRequest {
  nameZh: string;
  nameEn: string;
  descriptionZh?: string;
  descriptionEn?: string;
  dailyRate: number;
  duration: number;
  minAmount: number;
  maxAmount?: number;
  sortOrder?: number;
}

export interface UpdateProductRequest {
  nameZh?: string;
  nameEn?: string;
  descriptionZh?: string;
  descriptionEn?: string;
  dailyRate?: number;
  duration?: number;
  minAmount?: number;
  maxAmount?: number;
  sortOrder?: number;
}

// ============ Custody API functions ============

export async function getAdminCustodyProducts(params?: { page?: number; pageSize?: number; status?: string }) {
  return request<{ success: boolean; message: string; data?: CustodyProductListData }>(`${QUANT_API_URL}/api/admin/custody/products`, { method: 'GET', params, skipErrorHandler: true });
}

export async function createCustodyProduct(data: CreateProductRequest) {
  return request<{ success: boolean; message: string; data?: CustodyProduct }>(`${QUANT_API_URL}/api/admin/custody/products`, { method: 'POST', data, skipErrorHandler: true });
}

export async function updateCustodyProduct(id: string, data: UpdateProductRequest) {
  return request<{ success: boolean; message: string; data?: CustodyProduct }>(`${QUANT_API_URL}/api/admin/custody/products/${id}`, { method: 'PUT', data, skipErrorHandler: true });
}

export async function updateCustodyProductStatus(id: string, status: string) {
  return request<{ success: boolean; message: string }>(`${QUANT_API_URL}/api/admin/custody/products/${id}/status`, { method: 'PATCH', data: { status }, skipErrorHandler: true });
}

export async function getAdminCustodyOrders(params?: { page?: number; pageSize?: number; userId?: string; walletAddress?: string; status?: string }) {
  return request<{ success: boolean; message: string; data?: CustodyOrderListData }>(`${QUANT_API_URL}/api/admin/custody/orders`, { method: 'GET', params, skipErrorHandler: true });
}

export async function getCustodyStats() {
  return request<{ success: boolean; message: string; data?: CustodyStats }>(`${QUANT_API_URL}/api/admin/custody/stats`, { method: 'GET', skipErrorHandler: true });
}

export async function adminCancelCustodyOrder(id: string, remark?: string) {
  return request<{ success: boolean; message: string; data?: { orderId: string; userId: string; amount: number; newBalance: string } }>(`${QUANT_API_URL}/api/admin/custody/orders/${id}/cancel`, { method: 'POST', data: { remark: remark || '' }, skipErrorHandler: true });
}

// ============ Withdrawal types ============

export interface WithdrawalOrder {
  id: string;
  userId: string;
  walletAddress: string;
  amount: string;
  amountFormatted: string;
  status: 'pending' | 'approved' | 'rejected';
  txHash?: string;
  remark?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WithdrawalListData {
  orders: WithdrawalOrder[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminWithdrawalQuery {
  page?: number;
  pageSize?: number;
  walletAddress?: string;
  status?: string;
  userId?: string;
}

export interface BatchApproveResult {
  succeeded: number;
  failed: number;
  errors?: string[];
}

// ============ Withdrawal API functions ============

export async function getAdminWithdrawals(params?: AdminWithdrawalQuery) {
  return request<{ success: boolean; message: string; data?: WithdrawalListData }>(`${QUANT_API_URL}/api/admin/withdrawals`, { method: 'GET', params, skipErrorHandler: true });
}

export async function approveWithdrawal(id: string, txHash: string) {
  return request<{ success: boolean; message: string }>(`${QUANT_API_URL}/api/admin/withdrawals/${id}/approve`, { method: 'POST', data: { txHash }, skipErrorHandler: true });
}

export async function rejectWithdrawal(id: string, remark: string) {
  return request<{ success: boolean; message: string }>(`${QUANT_API_URL}/api/admin/withdrawals/${id}/reject`, { method: 'POST', data: { remark }, skipErrorHandler: true });
}

export async function batchApproveWithdrawals(items: { id: string; txHash: string }[]) {
  return request<{ success: boolean; message: string; data?: BatchApproveResult }>(`${QUANT_API_URL}/api/admin/withdrawals/batch-approve`, { method: 'POST', data: { items }, skipErrorHandler: true });
}

export async function exportWithdrawals(params?: { walletAddress?: string; status?: string }) {
  return request(`${QUANT_API_URL}/api/admin/withdrawals/export`, {
    method: 'GET',
    params,
    responseType: 'blob',
    skipErrorHandler: true,
  });
}

// ============ Commission types ============

export interface CommissionLevelConfig {
  level: number;
  name: string;
  threshold: number;
  rate: number; // percentage display value, e.g. 1 = 1%
}

export interface CommissionConfig {
  levels: CommissionLevelConfig[];
  directReferralRate: number; // percentage display value, e.g. 1 = 1%
  updatedAt?: string;
}

export interface CommissionRecord {
  id: string;
  orderId?: string;
  triggerUserId: string;
  beneficiaryId: string;
  rewardType: 'direct' | 'level_diff';
  sourceType: string;
  baseAmount: number;
  rate: number;
  amount: number;
  status: 'pending' | 'credited' | 'failed';
  beneficiaryWallet?: string;
  triggerUserWallet?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CommissionRecordListData {
  records: CommissionRecord[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CommissionStats {
  totalCommissions: number;
  totalCount: number;
  directCount: number;
  directTotal: number;
  levelDiffCount: number;
  levelDiffTotal: number;
}

export interface CommissionRecordQuery {
  page?: number;
  pageSize?: number;
  userId?: string;
  rewardType?: string;
  sourceType?: string;
  status?: string;
}

export interface UserLevelInfo {
  userId: string;
  level: string;
  totalPerformance: number;
  largeArea: number;
  smallArea: number;
  directReferrals?: DirectReferral[];
}

export interface DirectReferral {
  userId: string;
  walletAddress?: string;
  level: string;
  performance: number;
}

export interface UserEarnings {
  userId: string;
  totalEarnings: number;
  directEarnings: number;
  levelDiffEarnings: number;
  pendingEarnings: number;
}

// ============ Commission API functions ============

export async function getCommissionConfig(): Promise<{ success: boolean; message: string; data?: CommissionConfig }> {
  const res = await request<{ success: boolean; message: string; data?: any }>(`${QUANT_API_URL}/api/admin/commission/config`, { method: 'GET', skipErrorHandler: true });
  if (res?.success && res?.data) {
    // Transform backend fields: sLevelConfigs → levels, decimal rate → percentage
    res.data = {
      levels: (res.data.sLevelConfigs || []).map((c: any) => ({
        level: c.level,
        name: c.name,
        threshold: c.threshold,
        rate: c.rate * 100, // 0.01 → 1
      })),
      directReferralRate: (res.data.directReferralRate || 0) * 100, // 0.01 → 1
      updatedAt: res.data.updatedAt,
    };
  }
  return res;
}

export async function updateCommissionConfig(data: { levels: CommissionLevelConfig[]; directReferralRate: number }) {
  // Transform back: levels → sLevelConfigs, percentage → decimal
  const payload = {
    sLevelConfigs: data.levels.map((c) => ({
      level: c.level,
      name: c.name || `S${c.level}`,
      threshold: c.threshold,
      rate: c.rate / 100, // 1 → 0.01
    })),
    directReferralRate: data.directReferralRate / 100, // 1 → 0.01
  };
  return request<{ success: boolean; message: string; data?: CommissionConfig }>(`${QUANT_API_URL}/api/admin/commission/config/global`, { method: 'PUT', data: payload, skipErrorHandler: true });
}

export async function getCommissionRecords(params?: CommissionRecordQuery) {
  return request<{ success: boolean; message: string; data?: CommissionRecordListData }>(`${QUANT_API_URL}/api/admin/commission/records`, { method: 'GET', params, skipErrorHandler: true });
}

export async function getCommissionStats() {
  return request<{ success: boolean; message: string; data?: CommissionStats }>(`${QUANT_API_URL}/api/admin/commission/stats`, { method: 'GET', skipErrorHandler: true });
}

// ============ Commission Vesting (按月归属) ============

export type VestingStatus = 'pending' | 'releasing' | 'released' | 'forfeited' | 'skipped';

export interface CommissionVesting {
  id: string;
  orderId: string;
  triggerUserId: string;
  beneficiaryId: string;
  rewardType: 'direct' | 'level_diff';
  tranche: number;
  unlockAt: string;
  baseAmount: string;
  baseAmountFormatted?: string;
  snapshotRate: number;
  status: VestingStatus;
  releasedAt?: string;
  releasedAmount?: string;
  releasedAmountFormatted?: string;
  releasedRate?: number;
  recordId?: string;
  forfeitedAt?: string;
  forfeitReason?: string;
  forfeitByAdmin?: string;
  forfeitRemark?: string;
  attemptCount?: number;
  lastError?: string;
  beneficiaryWallet?: string;
  triggerUserWallet?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VestingListData {
  items: CommissionVesting[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AdminVestingQuery {
  page?: number;
  pageSize?: number;
  orderId?: string;
  triggerUserId?: string;
  beneficiaryId?: string;
  rewardType?: string;
  status?: string;
}

export async function getAdminVestings(params?: AdminVestingQuery) {
  return request<{ success: boolean; message: string; data?: VestingListData }>(
    `${QUANT_API_URL}/api/admin/commission/vesting`,
    { method: 'GET', params, skipErrorHandler: true },
  );
}

export async function adminForfeitVesting(id: string, remark?: string) {
  return request<{ success: boolean; message: string }>(
    `${QUANT_API_URL}/api/admin/commission/vesting/${id}/forfeit`,
    { method: 'POST', data: { remark: remark || '' }, skipErrorHandler: true },
  );
}

export async function adminRestoreVesting(id: string, remark?: string) {
  return request<{ success: boolean; message: string }>(
    `${QUANT_API_URL}/api/admin/commission/vesting/${id}/restore`,
    { method: 'POST', data: { remark: remark || '' }, skipErrorHandler: true },
  );
}

export async function getUserLevel(userId: string) {
  return request<{ success: boolean; message: string; data?: UserLevelInfo }>(`${QUANT_API_URL}/api/admin/commission/users/${userId}/level`, { method: 'GET', skipErrorHandler: true });
}

export async function setUserLevel(userId: string, level: string) {
  // Backend expects integer level (0-7) or null to clear custom level
  // Frontend uses "S0"-"S7" strings or "auto" to clear
  const data = level === 'auto'
    ? { level: null }
    : { level: parseInt(level.replace(/^S/i, ''), 10) };
  return request<{ success: boolean; message: string }>(`${QUANT_API_URL}/api/admin/commission/users/${userId}/level`, { method: 'PUT', data, skipErrorHandler: true });
}

export async function setCustomRate(userId: string, data: { customRate?: number | null; customDirectRate?: number | null; remark?: string }) {
  return request<{ success: boolean; message: string; data?: any }>(`${QUANT_API_URL}/api/admin/commission/users/${userId}/custom-rate`, { method: 'PUT', data, skipErrorHandler: true });
}

export async function getUserEarnings(userId: string) {
  return request<{ success: boolean; message: string; data?: UserEarnings }>(`${QUANT_API_URL}/api/admin/commission/users/${userId}/earnings`, { method: 'GET', skipErrorHandler: true });
}

export async function recalculatePerformance() {
  return request<{ success: boolean; message: string }>(`${QUANT_API_URL}/api/admin/commission/recalculate`, { method: 'POST', skipErrorHandler: true });
}

export async function unbindReferral(userId: string) {
  return request<{ success: boolean; message: string; data?: { userId: string; oldReferrerId: string } }>(`${QUANT_API_URL}/api/admin/referral/users/${userId}/unbind`, { method: 'POST', skipErrorHandler: true });
}

// ============ Finance stats types ============

export interface FinanceStats {
  totalDeposits: number;
  totalYieldClaimed: number;
  totalPrincipalWithdrawn: number;
  totalWithdrawals: number;
  totalCustodyPurchase: number;
  totalReferralClaimed: number;
  depositCount: number;
  yieldClaimCount: number;
  principalWithdrawCount: number;
  withdrawalCount: number;
  custodyPurchaseCount: number;
  referralClaimCount: number;
}

// Get finance overview statistics
export async function getFinanceStats() {
  return request<{ success: boolean; message: string; data?: FinanceStats }>(`${QUANT_API_URL}/api/admin/finance/stats`, { method: 'GET', skipErrorHandler: true });
}

// ============ Team query types ============

export interface AdminTeamMember {
  userId: string;
  walletAddress: string;
  sLevel: number;
  sLevelName: string;
  totalPerformance: string;
  smallAreaPerformance: string;
  totalEarnings: number;
  directReferralCount: number;
  hasActiveCustody: boolean;
  referrerId: string;
  depth: number;
  joinedAt: string;
}

export interface AdminTeamResponse {
  user: {
    userId: string;
    walletAddress: string;
    sLevel: number;
    sLevelName: string;
    totalPerformance: string;
    smallAreaPerformance: string;
    largestLinePerformance: string;
    directReferralCount: number;
    customSLevel?: number | null;
    customRate?: number;
    customDirectRate?: number;
    customRateRemark?: string;
    referredBy?: string;
    referredByCode?: string;
    referrerWallet?: string;
  };
  earnings: {
    totalEarnings: number;
    directEarnings: number;
    levelDiffEarnings: number;
  };
  teamSummary: {
    totalMembers: number;
    levelDistribution: Record<string, number>;
  };
  members: AdminTeamMember[];
}

// ============ Team query API ============

export async function getAdminUserTeam(userId: string) {
  return request<{ success: boolean; message: string; data?: AdminTeamResponse }>(`${QUANT_API_URL}/api/admin/commission/users/${userId}/team`, { method: 'GET', skipErrorHandler: true });
}
